import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  Download, Plus, Trash2, ArrowDownUp, Sparkles,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users,
} from "lucide-react";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton, BalancedBadge, RailSectionHeader } from "./_shared";

export function TrialBalanceReport({ currency }) {
  const [asOf, setAsOf] = useState(new Date());
  const [rawData, setRawData] = useState(null);
  const [drill, setDrill] = useState(null);
  const { filterRails, activeRail } = useCurrencyRail();
  const load = useCallback(async () => {
    const { data } = await api.get("/accounting/trial-balance", { params: { as_of: fmtISO(asOf) } });
    setRawData(data);
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  if (!rawData) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  // Rail filter also applies to row-level `currency` for TB row visibility
  const filteredByCcy = filterRails(rawData.by_currency || []);
  const data = {
    ...rawData,
    by_currency: filteredByCcy,
    rows: activeRail === "ALL"
      ? rawData.rows
      : rawData.rows.filter((r) => (r.currency || currency) === activeRail),
  };

  const multi = Array.isArray(data.by_currency) && data.by_currency.length > 1;

  // Map TB row → drill params. The `group` tells us how it was aggregated.
  const paramsFor = (r) => {
    const g = r.group || "";
    const acct = r.account || "";
    if (g.startsWith("Assets · Wallet")) return null;
    if (g.startsWith("Assets · Savings")) {
      const cat = acct.replace(/^Savings — /, "");
      return { kind: "savings_bucket", category: cat, as_of: fmtISO(asOf) };
    }
    if (g === "Expenses") return { kind: "expense", category: acct, as_of: fmtISO(asOf) };
    if (g === "Income") return { kind: "income", category: acct, as_of: fmtISO(asOf) };
    return null;
  };

  const renderRailTable = (railCcy, rows, railTotals) => {
    const groups = {};
    for (const r of rows) (groups[r.group] = groups[r.group] || []).push(r);
    return (
      <div key={`tb-rail-${railCcy}`} data-testid={`tb-rail-${railCcy}`} className="card-flat overflow-hidden border-l-4"
           style={{ borderLeftColor: railCcy === "USD" ? "#1E5A96" : "#4C956C" }}>
        <div className="px-5 py-3 border-b border-[var(--divider)] flex items-baseline justify-between">
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{railCcy} rail</div>
          {railTotals && (
            <BalancedBadge balanced={railTotals.balanced} difference={railTotals.difference} currency={railCcy} />
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)]">
            <tr className="text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <th className="py-2.5 px-5 text-left">Account</th>
              <th className="py-2.5 px-5 text-right w-40">Debit</th>
              <th className="py-2.5 px-5 text-right w-40">Credit</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([group, groupRows]) => (
              <Fragment key={`${railCcy}-${group}`}>
                <tr className="bg-[var(--hover)]/50">
                  <td colSpan={3} className="py-1.5 px-5 text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{group}</td>
                </tr>
                {groupRows.map((r, idx) => {
                  const drillParams = paramsFor(r);
                  return (
                    <tr
                      key={`${railCcy}-${group}-${r.account}-${idx}`}
                      data-testid={`tb-row-${railCcy}-${r.account}`}
                      onClick={drillParams ? () => setDrill({
                        title: `${r.account} · ${formatCurrency(r.debit || r.credit, railCcy)}`,
                        params: drillParams,
                      }) : undefined}
                      className={`border-b border-[var(--divider)] last:border-none ${drillParams ? "cursor-pointer hover:bg-[var(--row-hover)]" : ""}`}
                    >
                      <td className="py-2 px-5">{r.account}</td>
                      <td className="py-2 px-5 text-right font-mono-data">{r.debit > 0 ? formatCurrency(r.debit, railCcy) : "—"}</td>
                      <td className="py-2 px-5 text-right font-mono-data">{r.credit > 0 ? formatCurrency(r.credit, railCcy) : "—"}</td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
            {railTotals && (
              <tr className="border-t-2 border-[#2F4F4F] font-semibold bg-[var(--hover)]">
                <td className="py-3 px-5">Totals</td>
                <td data-testid={`tb-total-debit-${railCcy}`} className="py-3 px-5 text-right font-mono-data">{formatCurrency(railTotals.debit, railCcy)}</td>
                <td data-testid={`tb-total-credit-${railCcy}`} className="py-3 px-5 text-right font-mono-data">{formatCurrency(railTotals.credit, railCcy)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="tb-report" className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <DateField label="As of" value={asOf} onChange={setAsOf} testId="tb-asof" />
        <div className="flex items-center gap-2">
          {multi ? (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${
              data.by_currency.every((r) => r.balanced) ? "bg-[#EAF4EE] text-[#2F6B4A]" : "bg-[#FCE7E9] text-[#B4283A]"
            }`}>
              {data.by_currency.every((r) => r.balanced) ? "All rails balanced" : "One or more rails off"}
            </span>
          ) : (
            <BalancedBadge balanced={data.balanced} difference={data.totals.difference} currency={currency} />
          )}
          <CsvButton testId="tb-csv" onClick={() => downloadStream("/accounting/trial-balance.csv", { as_of: fmtISO(asOf) }, `trial_balance_${fmtISO(asOf)}.csv`)} />
        </div>
      </div>

      {multi ? (
        <>
          <ConsolidatedRailCard
            testId="tb-consolidated"
            subtitle="flip the sidebar FX toggle to hide"
            rails={data.by_currency.map((r) => ({
              currency: r.currency,
              debit: r.debit,
              credit: r.credit,
            }))}
            fields={[
              { key: "debit", label: "Total Debit", color: "text-[#2F4F4F]" },
              { key: "credit", label: "Total Credit", color: "text-[#2F4F4F]" },
            ]}
          />
          {data.by_currency.map((rail) => {
            const rows = data.rows.filter((r) => (r.currency || currency) === rail.currency);
            return (
              <div key={`tb-rail-wrap-${rail.currency}`}>
                <RailSectionHeader
                  currency={rail.currency}
                  subtitle="Every debit must equal every credit"
                  testId={`tb-rail-header-${rail.currency}`}
                  right={
                    <BalancedBadge
                      balanced={rail.balanced}
                      difference={rail.difference}
                      currency={rail.currency}
                    />
                  }
                />
                <div className="mt-2">
                  {renderRailTable(rail.currency, rows, rail)}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        renderRailTable(currency, data.rows, {
          debit: data.totals.debit,
          credit: data.totals.credit,
          difference: data.totals.difference,
          balanced: data.balanced,
        })
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={currency}
      />
    </div>
  );
}
